import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Save, X, MessageSquare, ChevronRight, ChevronLeft, Bot, User, FileText } from "lucide-react";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { PrettyEditor } from "./PrettyEditor";
import { CommentsListCard } from "./CommentsListCard";
import { ChangeSummary } from "./ChangeSummary";
import { ConnectorLines } from "./ConnectorLines";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { diffWords } from "diff";
import { Message } from "@/types/message";

interface Change {
  id: string;
  type: "addition" | "deletion" | "replace";
  text: string;
  originalText?: string;
  position: number;
  accepted?: boolean;
  context_before?: string;
  context_after?: string;
}

interface TextSelection {
  start: number;
  end: number;
  text: string;
  originalText: string;
  positions?: {
    edit: { from: number; to: number };
    readonly: { start: number; end: number };
    mdcode: { 
      startLine: number; 
      startChar: number; 
      endLine: number; 
      endChar: number; 
    };
  };
}

interface Comment {
  id: string;
  text: string;
  color: string;
  number: number;
  textSelections: TextSelection[];
  isGeneral: boolean;
  textChanged?: boolean;
  context_before?: string;
  context_after?: string;
}

interface MessageEditorCardProps {
  message: Message;
  onCancel: () => void;
  onSave: (latestContent: string, changes: Change[], comments: Comment[]) => void;
  onSummarize: (summary: string) => void;
  onSend: (summary: string) => void;
}

export const MessageEditorCard = ({ message, onCancel, onSave, onSummarize, onSend }: MessageEditorCardProps) => {
  const [originalContent] = useState(message.content); // Store original for cancel
  const [latestContent, setLatestContent] = useState(message.content);
  const [changes, setChanges] = useState<Change[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [showComments, setShowComments] = useState(true);
  const [selectedText, setSelectedText] = useState("");
  const [selectionRange, setSelectionRange] = useState<{ start: number; end: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nextCommentNumber, setNextCommentNumber] = useState(1);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isComposingComment, setIsComposingComment] = useState(false);
  
  const editorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  const colorPalette = ['blue', 'amber', 'purple', 'green', 'pink', 'teal'];

  // Load existing comments and changes from database
  useEffect(() => {
    loadCommentsAndChanges();
  }, [message.id]);

  // Auto-save with debounce - max 15 seconds of unsaved work
  useEffect(() => {
    if (hasUnsavedChanges && !isLoading) {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      
      // Set new timer for 15 seconds
      autoSaveTimerRef.current = setTimeout(() => {
        handleSave();
        setHasUnsavedChanges(false);
      }, 15000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, isLoading, latestContent, comments]);

  // Track when content changes and compute diffs using proper diff algorithm
  useEffect(() => {
    if (!isLoading && latestContent !== originalContent) {
      console.log('[MessageEditor] Content changed, computing diff');
      setHasUnsavedChanges(true);
      
      // Use proper word-level diff algorithm
      const newChanges: Change[] = [];
      const diffs = diffWords(originalContent, latestContent);
      console.log('[MessageEditor] Diff parts:', diffs.length);
      
      let originalPos = 0;
      let latestPos = 0;
      
      for (let i = 0; i < diffs.length; i++) {
        const part = diffs[i];
        
        if (part.removed && i + 1 < diffs.length && diffs[i + 1].added) {
          // This is a replacement: deletion followed by addition
          const removedText = part.value;
          const addedText = diffs[i + 1].value;
          
          // Get context from original content
          const beforeStart = Math.max(0, originalPos - 50);
          const beforeText = originalContent.slice(beforeStart, originalPos);
          const afterEnd = Math.min(originalContent.length, originalPos + removedText.length + 50);
          const afterText = originalContent.slice(originalPos + removedText.length, afterEnd);
          
          newChanges.push({
            id: `change-${Date.now()}-${newChanges.length}`,
            type: 'replace',
            text: addedText,
            originalText: removedText,
            position: originalPos,
            accepted: false,
            context_before: beforeText,
            context_after: afterText,
          });
          
          originalPos += removedText.length;
          latestPos += addedText.length;
          i++; // Skip the next added part since we handled it
        } else if (part.added) {
          // Pure addition
          const addedText = part.value;
          
          // Get context from original content
          const beforeStart = Math.max(0, originalPos - 50);
          const beforeText = originalContent.slice(beforeStart, originalPos);
          const afterEnd = Math.min(originalContent.length, originalPos + 50);
          const afterText = originalContent.slice(originalPos, afterEnd);
          
          newChanges.push({
            id: `change-${Date.now()}-${newChanges.length}`,
            type: 'addition',
            text: addedText,
            position: originalPos,
            accepted: false,
            context_before: beforeText,
            context_after: afterText,
          });
          
          latestPos += addedText.length;
        } else if (part.removed) {
          // Pure deletion
          const removedText = part.value;
          
          // Get context from original content
          const beforeStart = Math.max(0, originalPos - 50);
          const beforeText = originalContent.slice(beforeStart, originalPos);
          const afterEnd = Math.min(originalContent.length, originalPos + removedText.length + 50);
          const afterText = originalContent.slice(originalPos + removedText.length, afterEnd);
          
          newChanges.push({
            id: `change-${Date.now()}-${newChanges.length}`,
            type: 'deletion',
            text: removedText,
            position: originalPos,
            accepted: false,
            context_before: beforeText,
            context_after: afterText,
          });
          
          originalPos += removedText.length;
        } else {
          // Unchanged text
          originalPos += part.value.length;
          latestPos += part.value.length;
        }
      }
      
      console.log('[MessageEditor] Computed changes:', newChanges.length, 'changes');
      console.log('[MessageEditor] Changes breakdown:', {
        additions: newChanges.filter(c => c.type === 'addition').length,
        deletions: newChanges.filter(c => c.type === 'deletion').length,
        replacements: newChanges.filter(c => c.type === 'replace').length,
      });
      setChanges(newChanges);
    }
  }, [latestContent, originalContent, isLoading]);

  const loadCommentsAndChanges = async () => {
    console.log('[MessageEditor] Loading comments and changes for message:', message.id);
    try {
      // Load comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select('*')
        .eq('message_id', message.id);

      if (commentsError) throw commentsError;
      console.log('[MessageEditor] Loaded comments:', commentsData?.length || 0);

      if (commentsData && commentsData.length > 0) {
        const loadedComments = commentsData.map(c => {
          const textSelection = c.text_selection as any;
          return {
            id: c.id,
            text: c.comment_text,
            color: c.color || 'blue',
            number: c.number || 1,
            textSelections: Array.isArray(textSelection) ? textSelection : (textSelection ? [textSelection] : []),
            isGeneral: !textSelection,
            textChanged: false,
            context_before: c.context_before,
            context_after: c.context_after,
          };
        });
        setComments(loadedComments);
        const maxNumber = Math.max(...loadedComments.map(c => c.number));
        setNextCommentNumber(maxNumber + 1);
      }

      // Load track changes
      const { data: changesData, error: changesError } = await supabase
        .from('track_changes')
        .select('*')
        .eq('message_id', message.id);

      if (changesError) throw changesError;
      console.log('[MessageEditor] Loaded track changes:', changesData?.length || 0);

      if (changesData) {
        const loadedChanges = changesData
          .map(c => ({
            id: c.id,
            type: c.change_type as "addition" | "deletion" | "replace",
            text: c.change_type === 'replace' ? (c.new_text || '') : (c.change_type === 'addition' ? c.new_text : c.original_text) || '',
            originalText: c.change_type === 'replace' ? (c.original_text || '') : undefined,
            position: c.position,
            accepted: false,
            context_before: c.context_before,
            context_after: c.context_after,
          }))
          .filter(c => c.text.trim() !== ''); // Only show actual changes
        setChanges(loadedChanges);
      }
    } catch (error) {
      console.error('[MessageEditor] Error loading comments and changes:', error);
      toast.error('Failed to load existing comments and changes');
    } finally {
      console.log('[MessageEditor] Finished loading, setting isLoading to false');
      setIsLoading(false);
    }
  };


  const handleAddComment = async (commentText: string) => {
    console.log('[MessageEditorCard] Adding comment:', { 
      text: commentText.substring(0, 50) + (commentText.length > 50 ? '...' : ''),
      hasSelection: !!selectedText,
      selectedText: selectedText.substring(0, 50) + (selectedText.length > 50 ? '...' : ''),
      selectionRange,
      isComposingComment
    });
    const textSelection = selectedText && selectionRange ? {
      start: selectionRange.start,
      end: selectionRange.end,
      text: selectedText,
      originalText: selectedText,
    } : null;
    
    // Assign color: use a different color only if needed for overlapping selections
    const assignedColor = colorPalette[(nextCommentNumber - 1) % colorPalette.length];
    
    const newComment: Comment = {
      id: Date.now().toString(),
      text: commentText,
      color: assignedColor,
      number: nextCommentNumber,
      textSelections: textSelection ? [textSelection] : [],
      isGeneral: !textSelection,
      textChanged: false,
    };
    console.log('[MessageEditorCard] Created comment:', {
      id: newComment.id,
      isGeneral: newComment.isGeneral,
      hasTextSelections: newComment.textSelections.length > 0
    });
    setComments([...comments, newComment]);
    setNextCommentNumber(nextCommentNumber + 1);
    
    // Clear selection and composing state after adding comment
    console.log('[MessageEditorCard] Clearing selection and composing state');
    setSelectedText("");
    setSelectionRange(null);
    setIsComposingComment(false);
    
    // Auto-save immediately when adding comment
    setHasUnsavedChanges(true);
    setTimeout(() => handleSave(), 100);
  };

  const handleTextSelect = (text: string, range: { start: number; end: number }) => {
    console.log('[MessageEditorCard] handleTextSelect called:', {
      text: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
      range,
      isComposingComment,
      willUpdate: !isComposingComment
    });
    // Don't update selection while composing a comment to keep the highlight locked
    if (!isComposingComment) {
      setSelectedText(text);
      setSelectionRange(range);
      console.log('[MessageEditorCard] Selection updated');
    } else {
      console.log('[MessageEditorCard] Selection blocked - composing comment');
    }
  };

  const handleTextReplace = (originalText: string, newText: string, position: number) => {
    // This function is no longer needed as diffWords handles replacements
    // Keep it for backward compatibility but don't create duplicate changes
    setHasUnsavedChanges(true);
  };

  const handleDeleteComment = (commentId: string) => {
    console.log('[MessageEditor] Deleting comment:', commentId);
    setComments(comments.filter(c => c.id !== commentId));
  };

  const handleEditComment = async (commentId: string, newText: string) => {
    console.log('[MessageEditor] Editing comment:', { commentId, newText });
    setComments(comments.map(c => 
      c.id === commentId ? { ...c, text: newText } : c
    ));
    
    // Auto-save immediately when editing comment
    setHasUnsavedChanges(true);
    setTimeout(() => handleSave(), 100);
  };

  const handleHighlightClick = (commentId: string) => {
    // Highlight the comment
    setHighlightedCommentId(commentId);
    
    // Find the comment element and scroll to it
    const commentElement = document.getElementById(`comment-${commentId}`);
    if (commentElement) {
      commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    // Remove highlight after 2 seconds
    setTimeout(() => setHighlightedCommentId(null), 2000);
  };

  const handleSave = async (): Promise<boolean> => {
    console.log('[MessageEditor] Saving changes...', {
      messageId: message.id,
      commentsCount: comments.length,
      changesCount: changes.length,
      contentLength: latestContent.length,
    });
    try {
      // Update message editor_status to 'wip'
      console.log('[MessageEditor] Updating message status to WIP');
      await supabase
        .from('messages')
        .update({ editor_status: 'wip', content: latestContent })
        .eq('id', message.id);

      // Save comments to database
      // First delete existing non-deleted comments
      console.log('[MessageEditor] Deleting existing comments');
      await supabase
        .from('comments')
        .delete()
        .eq('message_id', message.id)
        .eq('deleted', false);

      // Insert new comments with context
      if (comments.length > 0) {
        console.log('[MessageEditor] Inserting', comments.length, 'comments');
        const commentsToInsert = comments.map(c => {
          let beforeSelection = '';
          let afterSelection = '';
          
          if (c.textSelections.length > 0) {
            const selection = c.textSelections[0];
            const start = selection.start;
            const end = selection.end;
            
            // Extract 50 chars before (or from start of document)
            beforeSelection = latestContent.substring(Math.max(0, start - 50), start);
            // Extract 50 chars after (or to end of document)
            afterSelection = latestContent.substring(end, Math.min(latestContent.length, end + 50));
          }
          
          return {
            message_id: message.id,
            comment_text: c.text,
            text_selection: (c.textSelections.length > 0 ? c.textSelections : []) as any,
            color: c.color,
            number: c.number,
            context_before: beforeSelection || null,
            context_after: afterSelection || null,
          };
        });

        const { error: commentsError } = await supabase
          .from('comments')
          .insert(commentsToInsert);

        if (commentsError) {
          console.error('[MessageEditor] Error inserting comments:', commentsError);
          throw commentsError;
        }
        console.log('[MessageEditor] Successfully inserted comments');
      }

      // Save track changes to database
      // First delete existing non-deleted changes
      console.log('[MessageEditor] Deleting existing track changes');
      await supabase
        .from('track_changes')
        .delete()
        .eq('message_id', message.id)
        .eq('deleted', false);

      // Insert new changes with context
      if (changes.length > 0) {
        console.log('[MessageEditor] Inserting', changes.length, 'track changes');
        const changesToInsert = changes.map(c => {
          const pos = c.position;
          const changeLength = c.text.length;
          
          // Extract 50 chars before (or from start of document)
          const beforeText = latestContent.substring(Math.max(0, pos - 50), pos);
          // Extract 50 chars after (or to end of document)
          const afterText = latestContent.substring(
            pos + changeLength, 
            Math.min(latestContent.length, pos + changeLength + 50)
          );
          
          return {
            message_id: message.id,
            change_type: c.type,
            original_text: c.type === 'deletion' ? c.text : (c.type === 'replace' ? c.originalText : null),
            new_text: c.type === 'addition' ? c.text : (c.type === 'replace' ? c.text : null),
            position: c.position,
            context_before: beforeText || null,
            context_after: afterText || null,
          };
        });

        console.log('[MessageEditor] Track changes to insert:', changesToInsert);
        const { error: changesError } = await supabase
          .from('track_changes')
          .insert(changesToInsert);

        if (changesError) {
          console.error('[MessageEditor] Error inserting track changes:', changesError);
          throw changesError;
        }
        console.log('[MessageEditor] Successfully inserted track changes');
      }

      console.log('[MessageEditor] Save completed successfully');
      onSave(latestContent, changes, comments);
      setHasUnsavedChanges(false);
      toast.success('Changes saved successfully');
      return true;
    } catch (error) {
      console.error('[MessageEditor] Error saving:', error);
      toast.error('Failed to save changes');
      return false;
    }
  };

  const handleSummarize = () => {
    const changesText = changes.length > 0 
      ? `Changes made:\n${changes.map(c => {
          if (c.type === 'replace') return `- Replaced "${c.originalText}" with "${c.text}"`;
          return `- ${c.type === 'addition' ? 'Added' : 'Deleted'}: "${c.text}"`;
        }).join('\n')}\n\n`
      : '';
    
    const commentsText = comments.length > 0
      ? `Comments:\n${comments.map(c => {
          const location = c.textSelections.length > 0 
            ? `On "${c.textSelections[0].text}": ` 
            : 'General: ';
          return `- ${location}${c.text}`;
        }).join('\n')}`
      : '';
    
    const summary = `${changesText}${commentsText}`.trim() || 'No changes or comments made.';
    onSummarize(summary);
  };

  return (
    <>
      {showComments && (
        <ConnectorLines comments={comments} editorRef={editorRef} panelRef={panelRef} />
      )}
      
      <Card className="p-0 bg-editor-bg border-2 border-primary/20 max-w-[calc(3rem+768px+320px)]">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              message.role === "assistant" ? "bg-primary/10" : "bg-muted"
            }`}>
              {message.role === "assistant" ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}
            </div>
            <span className="font-semibold">
              {message.role === "assistant" ? "Responding To Message" : "Editing Your Message"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowComments(!showComments)}
            >
              {showComments ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              {showComments ? "Hide" : "Show"} Comments
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                if (hasUnsavedChanges && !confirm('You have unsaved changes. Are you sure you want to close and lose these changes?')) {
                  return;
                }
                setLatestContent(originalContent);
                onCancel();
              }}
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button 
              size="sm" 
              onClick={handleSave}
              variant={hasUnsavedChanges ? "default" : "outline"}
            >
              <Save className="w-4 h-4 mr-1" />
              {hasUnsavedChanges ? "Save Draft *" : "Save Draft"}
            </Button>
          </div>
        </div>
        
        <ResizablePanelGroup direction="horizontal" className="min-h-[600px]">
          <ResizablePanel defaultSize={showComments ? 70 : 100} minSize={30}>
            <div 
              ref={editorRef} 
              className="h-full overflow-y-auto"
              onScroll={(e) => {
                if (panelRef.current) {
                  const target = e.currentTarget;
                  const scrollPercent = target.scrollTop / (target.scrollHeight - target.clientHeight || 1);
                  const panelScrollHeight = panelRef.current.scrollHeight;
                  const panelClientHeight = panelRef.current.clientHeight;
                  panelRef.current.scrollTop = scrollPercent * (panelScrollHeight - panelClientHeight);
                }
              }}
            >
              {isLoading ? (
                <div className="p-6 text-center text-muted-foreground">Loading...</div>
              ) : (
                <div className="p-6">
                  <PrettyEditor
                    content={latestContent}
                    onChange={setLatestContent}
                    onTextSelect={handleTextSelect}
                    onTextReplace={handleTextReplace}
                    comments={comments}
                    onHighlightClick={handleHighlightClick}
                  />
                </div>
              )}
            </div>
          </ResizablePanel>
          
          {showComments && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={30} minSize={20} maxSize={50}>
                <CommentsListCard
                  ref={panelRef}
                  comments={comments}
                  onAddComment={handleAddComment}
                  onDeleteComment={handleDeleteComment}
                  onEditComment={handleEditComment}
                  selectedText={selectedText}
                  highlightedCommentId={highlightedCommentId}
                  isComposingComment={isComposingComment}
                  setIsComposingComment={setIsComposingComment}
                />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
        
        <ChangeSummary
          messageId={message.id}
          originalContent={originalContent}
          latestContent={latestContent}
          changes={changes}
          comments={comments}
          onSend={async (summary) => {
            await handleSave();
            onSend(summary);
          }}
        />
      </Card>
    </>
  );
};
